import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'wouter';
import { 
  ArrowLeft, Star, Camera, Settings as SettingsIcon, 
  Crown, Edit3, TrendingUp, BarChart3, Users, Heart, 
  Trophy, Flame, Target, Award, Share2, Plus,
  Bell, LogOut, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Package, Wallet as WalletIcon,
  ShoppingBag, History, Bookmark as BookmarkIcon, CreditCard,
  ArrowUpRight, ArrowDownLeft, AlertCircle, Check, X, Search, Bookmark,
  List, Sparkles, Medal, BadgeCheck, Zap, Network, Map, MapPinned,
  HeartHandshake, UserSquare2, Store, FileText, ThumbsUp,
  ShieldCheck, UsersRound, Compass, UtensilsCrossed, HelpCircle, Loader2,
  RefreshCw, Truck, MoreVertical, Pencil, Trash2
} from 'lucide-react';
import { KudozIcon } from '@/components/KudozIcon';
import UserAvatar from '@/components/UserAvatar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import TopHeader from '../components/TopHeader';
import { cn, resolveEatzyMediaUrl } from '@/lib/utils';
import { AuthenticatedAvatarImage } from '@/components/AuthenticatedAvatarImage';
import { AuthenticatedVideo } from '@/components/AuthenticatedVideo';
import { proxyIfCdn } from '@/hooks/useAuthenticatedMedia';
import { formatDistanceToNow, format, startOfMonth } from 'date-fns';
import DraftReviewsManager from '../components/DraftReviewsManager';
import PostOrderReviewPrompt from '../components/PostOrderReviewPrompt';
import CreateReviewModal from '../components/CreateReviewModal';
import CommentsManager from '../components/CommentsManager';
import TrustBadge, { getTrustTier } from '@/components/TrustBadge';
import TrustInfoModal from '@/components/TrustInfoModal';
import { KudozSection } from '@/components/KudozSection';
import BadgeExplainerModal from '@/components/BadgeExplainerModal';
import FollowersListModal from '@/components/FollowersListModal';
import { ReorderValidationModal } from '@/components/ReorderValidationModal';
import { 
  getBadgeCategoryIcon, 
  getCategoryAccentColor, 
  getBadgeTierColor,
  getCategoryName,
  getAllBadgeCategories
} from '@/lib/badgeUtils';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { CUISINE_TAGS, DIETARY_RESTRICTIONS } from '@shared/constants';
import type { EnrichedPendingKudoz } from '@shared/schema';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/AuthService';
import { EatzyTokenExpiredError } from '@/hooks/useOrderHistory';
import { useUserProfile } from '@/hooks/useUserProfile';
import { generateDisplayName, getAvatarInitials } from '@shared/utils/displayName';

// Types for API responses
type UserPreferences = {
  showActivity: boolean | null;
  cuisinePreferences: Record<string, 'love' | 'ok' | 'avoid'> | null;
  dietaryRestrictions: string[] | null;
};

type NotificationPreferences = {
  pauseAll: boolean;
  locationSharing: boolean;
  profileVisibility: 'public' | 'friends' | 'private';
  orderConfirmationsPush: boolean;
  orderConfirmationsEmail: boolean;
  orderConfirmationsSms: boolean;
  orderStatusPush: boolean;
  orderStatusEmail: boolean;
  orderStatusSms: boolean;
  afterDiningPrompts: boolean;
  postOrderReminders: boolean;
  commentsReplies: boolean;
  mentions: boolean;
  newFollowers: boolean;
  kudozEarned: boolean;
  promotionalOffers: boolean;
  newRestaurantAlerts: boolean;
  appUpdatesNews: boolean;
};

type UserAlert = {
  id: string;
  category: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  isRead: boolean;
  createdAt: string;
};

type UserBookmark = {
  id: string;
  restaurantId: string;
  restaurant: {
    id: string;
    name: string;
    category: string;
    overallRating: number;
    totalReviews: number;
  };
};

type Badge = {
  id: string;
  category: string;
  name: string;
  description: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  iconName: string;
  requirementType: string;
  requirementValue: number;
  kudozReward: number;
};

type UserBadge = {
  id: string;
  userId: string;
  badgeId: string;
  unlockedAt: Date;
  badge: Badge;
};

type BadgeProgress = {
  badge: Badge;
  currentValue: number;
  thresholdValue: number;
  percentage: number;
  isUnlocked: boolean;
};

// Fixed base date for deterministic mock data
const MOCK_BASE_DATE = new Date('2024-01-15T12:00:00Z').getTime();

// Mock user data with creator-focused features
const mockCurrentUser = {
  id: 'current-user',
  firstName: 'Sarah',
  lastName: 'Chen',
  name: 'Sarah Chen',
  displayName: 'Sarah C.',
  username: '@sarahchen',
  location: 'Manhattan, NY',
  bio: 'Food enthusiast • Verified reviewer',
  avatar: '',
  trustScore: 89,
  trustTier: 'trusted' as const,
  kudoz: 1250,
  nextTierAt: 2000,
  currentTier: 'Gold',
  streak: 12,
  stats: {
    reviews: 42,
    followers: 128,
    following: 89,
    totalLikes: 1243,
    totalViews: 8567,
  },
  badges: [
    { id: 1, name: 'Top Reviewer', icon: Trophy, color: 'text-yellow-600', earned: true },
    { id: 2, name: 'Pizza Expert', icon: Award, color: 'text-orange-600', earned: true },
    { id: 3, name: 'Early Adopter', icon: Star, color: 'text-blue-600', earned: true },
    { id: 4, name: 'Influencer', icon: Crown, color: 'text-purple-600', earned: false },
  ],
};

export default function Profile() {
  const [, navigate] = useLocation();
  const { user: authUser, logout, refreshUser } = useAuth();
  const { profile: userProfile, refreshProfile, kudozBalance: externalKudozBalance } = useUserProfile();
  
  // Fetch unread message count for header
  const { data: unreadMessageData } = useQuery<{ count: number }>({
    queryKey: ['/api/user/unread-messages-count'],
    enabled: !!authUser,
    refetchInterval: 60000, // Refresh every minute
  });
  
  // Fetch user's reviews early (needed for totalLikes and topReviews)
  const { data: userReviews = [], isLoading: isLoadingReviews } = useQuery<any[]>({
    queryKey: ['/api/reviews/me'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/reviews/me', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!response.ok) {
        console.error('Failed to fetch user reviews:', response.status);
        return [];
      }
      return response.json();
    },
    refetchOnMount: 'always'
  });

  // Eatzy profile state
  const [eatzyProfile, setEatzyProfile] = useState<any>(null);
  const [eatzyLoading, setEatzyLoading] = useState(false);
  
  // Streak state
  const [streakData, setStreakData] = useState<{ currentStreak: number; longestStreak: number } | null>(null);
  
  // Fetch Eatzy profile when component mounts
  useEffect(() => {
    const fetchEatzyProfile = async () => {
      if (!authUser) return;
      
      // Check cache first
      const cached = authService.getCachedEatzyProfile();
      if (cached) {
        console.log('📦 Using cached Eatzy profile in Profile page');
        setEatzyProfile(cached);
        return;
      }
      
      setEatzyLoading(true);
      
      try {
        console.log('🔄 Fetching Eatzy profile in Profile page...');
        const profile = await authService.getEatzyProfile();
        setEatzyProfile(profile);
        console.log('✅ Eatzy profile loaded in Profile page:', profile);
      } catch (error) {
        console.warn('⚠️ Failed to fetch Eatzy profile in Profile page:', error);
        // Don't show error to user - Eatzy profile is optional
      } finally {
        setEatzyLoading(false);
      }
    };
    
    fetchEatzyProfile();
  }, [authUser]);
  
  // Fetch streak data when component mounts
  useEffect(() => {
    const fetchStreak = async () => {
      if (!authUser) return;
      
      try {
        const _tok = localStorage.getItem('auth_token');
        const response = await fetch('/api/streaks/current', {
          credentials: 'include',
          headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { 'Authorization': `Bearer ${_tok}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setStreakData({
              currentStreak: data.currentStreak || 0,
              longestStreak: data.longestStreak || 0
            });
          }
        }
      } catch (error) {
        console.warn('Failed to fetch streak data:', error);
      }
    };
    
    fetchStreak();
  }, [authUser]);
  
  // Use real user data from auth context + Eatzy profile, fallback to mock for missing fields
  // For social login users, eatzyProfile may be null (403) and authUser.firstName/lastName may
  // be undefined — fall back to splitting authUser.name or displayName as a last resort
  const rawAuthName = eatzyProfile ? '' : (authUser?.name || authUser?.displayName || '');
  const nameParts = rawAuthName.trim().split(/\s+/);
  const firstName = eatzyProfile?.firstname || authUser?.firstName || nameParts[0] || '';
  const lastName = eatzyProfile?.lastname || authUser?.lastName || nameParts.slice(1).join(' ') || '';
  const fullName = firstName && lastName 
    ? `${firstName} ${lastName}`.trim()
    : authUser?.name || authUser?.displayName || 'Unknown User';
  
  const user = authUser ? {
    ...mockCurrentUser, // Keep mock data for fields not available in API
    id: authUser.id || 'current-user',
    firstName,
    lastName,
    name: fullName,
    displayName: authUser.displayName || generateDisplayName(firstName, lastName, fullName),
    username: `@${authUser.email?.split('@')[0] || 'user'}`,
    location: eatzyProfile?.city && eatzyProfile?.state 
      ? `${eatzyProfile.city}, ${eatzyProfile.state}`
      : 'Location not set',
    bio: authUser.bio || '',
    avatar: authUser.avatar || '',
    kudoz: externalKudozBalance || 0,
    trustScore: userProfile?.trustScore ?? authUser.trustScore ?? 75,
    trustTier: userProfile?.trustTier ?? authUser.trustTier ?? 'newbie',
    streak: streakData?.currentStreak ?? 0,
    longestStreak: streakData?.longestStreak ?? 0,
    // Use real stats from API where available
    stats: {
      reviews: authUser?.totalReviews ?? 0,
      followers: (authUser as any)?.followersCount ?? 0,
      following: (authUser as any)?.followingCount ?? 0,
      totalLikes: userReviews.reduce((sum: number, r: any) => sum + (r.likes || 0), 0),
      totalViews: 0,
    }
  } : mockCurrentUser;
  const [activeTab, setActiveTab] = useState('orders');
  const [showDraftManager, setShowDraftManager] = useState(false);
  const [showPostOrderPrompt, setShowPostOrderPrompt] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [showCommentsManager, setShowCommentsManager] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalTab, setFollowersModalTab] = useState<"followers" | "following">("followers");
  const [selectedOrderForReview, setSelectedOrderForReview] = useState<any>(null);
  const [reorderModalData, setReorderModalData] = useState<{
    orderId: string;
    restaurantId: string;
    eatzyClientId: string;
    serviceType: 'takeout' | 'delivery';
    deliveryLat?: number;
    deliveryLng?: number;
    deliveryAddressId?: number;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showNotificationsPrivacy, setShowNotificationsPrivacy] = useState(false);
  const [showLists, setShowLists] = useState(true);
  const [showBadges, setShowBadges] = useState(false);
  const [showCreatorInsights, setShowCreatorInsights] = useState(false);
  const alertsRef = useRef<HTMLDivElement>(null);
  
  // Avatar upload state
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState<string>('');
  
  // Handle URL parameters to auto-open sections
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    
    if (section === 'comments') {
      setShowCommentsManager(true);
      // Clear the URL parameter after opening
      window.history.replaceState({}, '', '/profile');
    } else if (section === 'alerts') {
      // Switch to alerts tab (alerts is now a tab in the Activity section)
      setActiveTab('alerts');
      window.history.replaceState({}, '', '/profile');
    }
  }, []);
  
  // Default notification preferences
  const defaultNotificationPrefs: NotificationPreferences = {
    pauseAll: false,
    locationSharing: true,
    profileVisibility: 'public',
    orderConfirmationsPush: true,
    orderConfirmationsEmail: true,
    orderConfirmationsSms: false,
    orderStatusPush: true,
    orderStatusEmail: false,
    orderStatusSms: false,
    afterDiningPrompts: true,
    postOrderReminders: true,
    commentsReplies: true,
    mentions: true,
    newFollowers: true,
    kudozEarned: true,
    promotionalOffers: false,
    newRestaurantAlerts: false,
    appUpdatesNews: false,
  };

  // Fetch notification preferences from API
  const { data: notificationPrefs = defaultNotificationPrefs, isLoading: isLoadingNotificationPrefs } = useQuery<NotificationPreferences>({
    queryKey: ['/api/user/notification-preferences'],
    refetchOnMount: 'always',
  });

  const { toast } = useToast();

  // Mutation to update notification preferences
  const updateNotificationPrefsMutation = useMutation({
    mutationFn: async (prefs: Partial<NotificationPreferences>) => {
      const res = await apiRequest('PUT', '/api/user/notification-preferences', prefs);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/notification-preferences'] });
    },
    onError: (error) => {
      console.error('Failed to update notification preferences:', error);
      toast({ description: 'Failed to save notification preferences', variant: 'destructive' });
    }
  });

  // Helper to update a single preference
  const updateNotificationPref = (key: keyof NotificationPreferences, value: boolean | string) => {
    updateNotificationPrefsMutation.mutate({ [key]: value });
    toast({ description: 'Notification settings updated' });
  };
  
  // Pagination state for each tab (page-based)
  const ITEMS_PER_PAGE = 10;
  const INITIAL_TRANSACTIONS_LIMIT = 50; // Load more initially for grouping
  const [ordersPage, setOrdersPage] = useState(1);
  const [transactionsLimit, setTransactionsLimit] = useState(INITIAL_TRANSACTIONS_LIMIT);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [reviewsPage, setReviewsPage] = useState(1);
  const [alertsPage, setAlertsPage] = useState(1);

  // Review edit/delete state
  const [editingReview, setEditingReview] = useState<{ id: string; rating: number; text: string } | null>(null);
  const [deletingReviewId, setDeletingReviewId] = useState<string | null>(null);

  // Edit review mutation
  const editReviewMutation = useMutation({
    mutationFn: async (data: { id: string; rating: number; text: string }) => {
      const res = await apiRequest('PUT', `/api/reviews/${data.id}`, { rating: data.rating, text: data.text });
      if (!res.ok) throw new Error('Failed to update review');
      return res.json();
    },
    onSuccess: () => {
      setEditingReview(null);
      queryClient.invalidateQueries({ queryKey: ['/api/reviews/me'] });
      toast({ description: 'Review updated successfully' });
    },
    onError: () => {
      toast({ description: 'Failed to update review', variant: 'destructive' });
    },
  });

  // Delete review mutation
  const deleteReviewMutation = useMutation({
    mutationFn: async (reviewId: string) => {
      const res = await apiRequest('DELETE', `/api/reviews/${reviewId}`);
      if (!res.ok) throw new Error('Failed to delete review');
      return res.json();
    },
    onSuccess: () => {
      setDeletingReviewId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/reviews/me'] });
      toast({ description: 'Review deleted' });
    },
    onError: () => {
      toast({ description: 'Failed to delete review', variant: 'destructive' });
    },
  });

  // Fetch alerts - always refetch on mount to ensure fresh data
  const { data: alerts = [], isLoading: isLoadingAlerts } = useQuery<UserAlert[]>({
    queryKey: ['/api/user/alerts'],
    refetchOnMount: 'always'
  });

  // Fetch bookmarks - always refetch on mount
  const { data: bookmarks = [], isLoading: isLoadingBookmarks } = useQuery<UserBookmark[]>({
    queryKey: ['/api/user/bookmarks'],
    refetchOnMount: 'always'
  });

  // Fetch preferences
  const { data: preferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
    refetchOnMount: 'always'
  });
  
  // Fetch pending Kudoz - always refetch on mount
  const pendingKudozQuery = useQuery<EnrichedPendingKudoz[]>({
    queryKey: ['/api/kudoz/pending'],
    refetchOnMount: 'always'
  });
  
  // Fetch Kudoz transactions with dynamic limit - always refetch on mount
  const transactionsQuery = useQuery<{ transactions: any[]; total: number }>({
    queryKey: ['/api/kudoz/transactions', transactionsLimit],
    queryFn: async () => {
      const _tok = localStorage.getItem('auth_token');
      const response = await fetch(`/api/kudoz/transactions?limit=${transactionsLimit}&offset=0`, {
        credentials: 'include',
        headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { 'Authorization': `Bearer ${_tok}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch transactions');
      return response.json();
    },
    refetchOnMount: 'always'
  });

  // Helper: Group transactions by month
  const groupedTransactions = useMemo(() => {
    if (!transactionsQuery.data?.transactions) return [];
    
    const groups: Record<string, { monthKey: string; monthLabel: string; transactions: any[]; totalEarned: number; totalSpent: number }> = {};
    
    transactionsQuery.data.transactions.forEach((tx: any) => {
      const date = new Date(tx.createdAt);
      const monthKey = format(startOfMonth(date), 'yyyy-MM');
      const monthLabel = format(date, 'MMMM yyyy');
      
      if (!groups[monthKey]) {
        groups[monthKey] = { monthKey, monthLabel, transactions: [], totalEarned: 0, totalSpent: 0 };
      }
      
      groups[monthKey].transactions.push(tx);
      if (tx.amount > 0) {
        groups[monthKey].totalEarned += tx.amount;
      } else {
        groups[monthKey].totalSpent += Math.abs(tx.amount);
      }
    });
    
    // Sort by date (newest first)
    return Object.values(groups).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [transactionsQuery.data?.transactions]);

  // Helper: Calculate category summaries with earned/spent separated
  const categorySummaries = useMemo(() => {
    if (!transactionsQuery.data?.transactions) return [];
    
    const summaries: Record<string, { category: string; label: string; earned: number; spent: number; count: number }> = {};
    
    const categoryLabels: Record<string, string> = {
      'review': 'Reviews',
      'order': 'Orders',
      'badge': 'Badges',
      'profile': 'Profile',
      'service': 'Services',
      'gift_certificate': 'Gift Cards',
    };
    
    transactionsQuery.data.transactions.forEach((tx: any) => {
      const category = tx.category || 'other';
      if (!summaries[category]) {
        summaries[category] = {
          category,
          label: categoryLabels[category] || 'Other',
          earned: 0,
          spent: 0,
          count: 0
        };
      }
      if (tx.amount > 0) {
        summaries[category].earned += tx.amount;
      } else {
        summaries[category].spent += Math.abs(tx.amount);
      }
      summaries[category].count += 1;
    });
    
    // Sort by total earned descending
    return Object.values(summaries).sort((a, b) => b.earned - a.earned);
  }, [transactionsQuery.data?.transactions]);

  // Toggle month expansion
  const toggleMonthExpansion = (monthKey: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  };

  // Load more transactions
  const loadMoreTransactions = () => {
    setTransactionsLimit(prev => prev + 50);
  };

  // Fetch user's badges - always refetch on mount
  const { data: userBadges = [], isLoading: isLoadingBadges } = useQuery<UserBadge[]>({
    queryKey: ['/api/badges/user'],
    refetchOnMount: 'always'
  });

  // Fetch badge progress - always refetch on mount
  const { data: badgeProgress = [], isLoading: isLoadingProgress } = useQuery<BadgeProgress[]>({
    queryKey: ['/api/badges/progress'],
    refetchOnMount: 'always'
  });

  // Derive top performing reviews from real data, sorted by likes
  const topReviews = useMemo(() => {
    return [...userReviews]
      .sort((a, b) => (b.likes || 0) - (a.likes || 0))
      .slice(0, 3)
      .map(r => ({
        id: r.id,
        restaurant: r.restaurant?.name || 'Unknown Restaurant',
        likes: r.likes || 0,
        rating: r.rating || 0,
      }));
  }, [userReviews]);

  // Fetch user's order history from Eatzy Consumer API
  type EatzyOrderItem = {
    id: number;
    item_id: string;
    price: number;
    name: string;
    quantity: number;
    notes?: string;
    options?: Array<{
      id: number;
      answer_text: string;
      question_text?: string;
    }>;
  };
  
  type EatzyOrder = {
    id: string;
    type: 'delivery' | 'takeout';
    status: string;
    favorite?: boolean;
    rating?: number;
    reorder?: boolean;
    restaurant: {
      id: number;
      profile?: string;
      name: string;
      logo?: string;
    };
    address?: {
      id: number;
      address: string;
      city: string;
      state: string;
      zipcode: string;
      latitude?: number;
      longitude?: number;
    };
    date: number; // Unix timestamp
    total: number;
    items: EatzyOrderItem[];
  };
  
  type EatzyOrderHistoryResponse = {
    total_results: number;
    total_pages: number;
    page: number;
    results: EatzyOrder[];
  };

  const { data: ordersResponse, isLoading: isLoadingOrders, isError: isOrdersError, error: ordersError } = useQuery<EatzyOrderHistoryResponse>({
    queryKey: ['/api/eatzy/consumer/customer/order/history', { page: 1 }],
    queryFn: async () => {
      const response = await authService.eatzyApiCall('/consumer/customer/order/history?page=1&query=');

      if (!response.ok) {
        if (response.status === 403) {
          try {
            const errData = await response.clone().json();
            if (errData.eatzyTokenExpired) throw new EatzyTokenExpiredError();
          } catch (inner) {
            if (inner instanceof EatzyTokenExpiredError) throw inner;
          }
        }
        const errorText = await response.text();
        console.error('[Orders] Error response:', errorText);
        throw new Error(`Failed to fetch orders: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Orders] Successfully fetched orders:', data?.total_results || 0, 'total');
      return data;
    },
    refetchOnMount: 'always',
    staleTime: 0,
    retry: 1
  });
  const isEatzyTokenExpired = ordersError instanceof EatzyTokenExpiredError;

  // Fetch reviewed order IDs and ratings from our database
  const { data: reviewedOrdersData } = useQuery<{ 
    orderIds: string[]; 
    orders: { orderId: string; rating: number }[] 
  }>({
    queryKey: ['/api/user/reviewed-order-ids'],
    enabled: !!authUser,
    staleTime: 30 * 1000, // 30 seconds
  });
  
  const reviewedOrderIds = new Set(reviewedOrdersData?.orderIds || []);
  // Create a lookup object of orderId -> rating for quick access
  const reviewedOrderRatings: Record<string, number> = (reviewedOrdersData?.orders || []).reduce(
    (acc, o) => { acc[o.orderId] = o.rating; return acc; },
    {} as Record<string, number>
  );
  
  // Track draft state reactively (orderId and whether draft exists)
  const [draftOrderId, setDraftOrderId] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  
  // Function to update draft state from localStorage
  const updateDraftState = () => {
    const savedDraft = localStorage.getItem('eatzy-review-draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setDraftOrderId(draft.orderId || null);
        setHasDraft(true);
      } catch {
        setDraftOrderId(null);
        setHasDraft(false);
      }
    } else {
      setDraftOrderId(null);
      setHasDraft(false);
    }
  };
  
  useEffect(() => {
    // Initial load
    updateDraftState();
    
    // Listen for storage events (changes from other tabs/components)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'eatzy-review-draft') {
        updateDraftState();
      }
    };
    
    // Listen for custom draft events (changes within same tab)
    const handleDraftChange = () => {
      updateDraftState();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('draftChanged', handleDraftChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('draftChanged', handleDraftChange);
    };
  }, []);

  // Log order statuses for debugging
  if (ordersResponse?.results?.length) {
    const statusCounts = (ordersResponse.results as EatzyOrder[]).reduce((acc, order) => {
      acc[order.status || 'undefined'] = (acc[order.status || 'undefined'] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    if (import.meta.env.DEV) {
      console.log('[Profile Orders] Status distribution:', statusCounts);
    }
  }

  // Transform Eatzy orders to match expected UI structure
  // Show ALL orders (active and completed) - no filtering by status
  const completedStatuses = ['closed', 'delivered', 'completed'];
  const orderHistory = (ordersResponse?.results || [])
    .map((order: EatzyOrder) => {
      // Check if this order has been reviewed in our system
      const hasReviewInOurSystem = reviewedOrderIds.has(String(order.id));
      const orderStatus = order.status?.toLowerCase() || 'unknown';
      const isCompleted = completedStatuses.includes(orderStatus);
      
      // Determine display-friendly order status
      const getOrderStatusLabel = (status: string): string => {
        switch (status.toLowerCase()) {
          case 'open': return 'In Progress';
          case 'confirmed': return 'Confirmed';
          case 'completed':
          case 'closed':
          case 'delivered': return 'Completed';
          default: return status.charAt(0).toUpperCase() + status.slice(1);
        }
      };
      
      // Review status only applies to completed orders
      const getReviewStatus = (): 'reviewed' | 'not_reviewed' | 'not_applicable' => {
        if (!isCompleted) return 'not_applicable';
        return hasReviewInOurSystem ? 'reviewed' : 'not_reviewed';
      };
      
      return {
        id: order.id,
        // Prefer profile (eatzyClientId UUID) over the raw integer ID so that
        // /api/restaurants/:id/checkout/start can resolve the restaurant via its
        // existing eatzyClientId fallback. Fall back to the integer ID string only
        // if profile is absent (defensive).
        restaurantId: order.restaurant?.profile || String(order.restaurant?.id || ''),
        eatzyClientId: order.restaurant?.profile || '',
        restaurantName: order.restaurant?.name || 'Unknown Restaurant',
        restaurantLogo: order.restaurant?.logo,
        restaurantLocation: order.address 
          ? `${order.address.city}, ${order.address.state}`
          : '',
        items: order.items?.map(item => item.name) || ['Order items'],
        fullItems: order.items?.map((item, idx) => ({
          lineItemId: item.id ? String(item.id) : `line_${order.id}_${idx}`,
          itemId: item.item_id || (item.id ? String(item.id) : `item_${order.id}_${idx}`),
          itemName: item.name,
          quantity: item.quantity || 1,
          price: item.price || 0,
          options: item.options?.map((opt: any) => opt.answer_text).filter(Boolean) || []
        })) || [],
        total: order.total || 0,
        orderStatus: orderStatus, // Raw status from API
        orderStatusLabel: getOrderStatusLabel(orderStatus), // Display-friendly label
        isCompleted: isCompleted,
        date: new Date(order.date * 1000), // Convert Unix timestamp to Date
        // Review status for display
        reviewStatus: getReviewStatus(),
        reviewScore: hasReviewInOurSystem ? reviewedOrderRatings[String(order.id)] : undefined,
        type: order.type as 'delivery' | 'takeout',
        canReorder: order.reorder,
        isFavorite: order.favorite,
        deliveryLat: order.address?.latitude ? Number(order.address.latitude) : undefined,
        deliveryLng: order.address?.longitude ? Number(order.address.longitude) : undefined,
        deliveryAddressId: order.address?.id ? Number(order.address.id) : undefined
      };
    });
  
  // Compute review center count (pending reviews + draft) avoiding double-counting
  const reviewCenterCount = useMemo(() => {
    // Count pending reviews (completed orders not yet reviewed)
    const pendingOrders = orderHistory.filter(o => o.reviewStatus === 'not_reviewed');
    let count = pendingOrders.length;
    
    // Add 1 for draft only if it's a dine-out draft (no orderId)
    // Order-based drafts have an orderId which is already counted in pendingOrders
    if (hasDraft && draftOrderId === null) {
      count += 1;
    }
    
    return count;
  }, [orderHistory, hasDraft, draftOrderId]);

  // Log transformed orders for debugging (development only)
  if (import.meta.env.DEV) {
    console.log('[Profile Orders] Transformed orders:', {
      totalOrders: orderHistory.length,
      completedOrders: orderHistory.filter(o => o.isCompleted).length,
      activeOrders: orderHistory.filter(o => !o.isCompleted).length,
      reviewedOrders: orderHistory.filter(o => o.reviewStatus === 'reviewed').length,
      notReviewedOrders: orderHistory.filter(o => o.reviewStatus === 'not_reviewed').length,
      statusBreakdown: orderHistory.reduce((acc, o) => {
        acc[o.orderStatus] = (acc[o.orderStatus] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      sampleOrders: orderHistory.slice(0, 3).map(o => ({
        id: o.id,
        orderStatus: o.orderStatus,
        orderStatusLabel: o.orderStatusLabel,
        reviewStatus: o.reviewStatus,
        isCompleted: o.isCompleted
      }))
    });
  }

  // Mark alert as read mutation
  const markAlertAsRead = useMutation({
    mutationFn: async (alertId: string) => {
      return await apiRequest('POST', `/api/alerts/${alertId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/alerts'] });
    }
  });

  // Delete alert mutation
  const deleteAlert = useMutation({
    mutationFn: async (alertId: string) => {
      return await apiRequest('DELETE', `/api/alerts/${alertId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/alerts'] });
    }
  });

  // Toggle bookmark mutation
  const toggleBookmark = useMutation({
    mutationFn: async (restaurantId: string) => {
      return await apiRequest('POST', '/api/bookmarks/toggle', { restaurantId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/bookmarks'] });
      toast({ description: 'Bookmark updated' });
    }
  });

  // Update preferences mutation
  const updatePreferences = useMutation({
    mutationFn: async (updates: { showActivity?: boolean; cuisinePreferences?: Record<string, 'love' | 'ok' | 'avoid'>; dietaryRestrictions?: string[] }) => {
      console.log('[Mutation] Calling API with updates:', updates);
      const result = await apiRequest('PUT', '/api/user/preferences', updates);
      console.log('[Mutation] API response:', result);
      return result;
    },
    onSuccess: async (data) => {
      console.log('[Mutation] onSuccess triggered with data:', data);
      await queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/user/alerts'] });
      await queryClient.refetchQueries({ queryKey: ['/api/user/preferences'] });
      console.log('[Mutation] About to show toast');
      toast({ description: 'Preferences updated' });
      console.log('[Mutation] Toast should have been shown');
    },
    onError: (error) => {
      console.error('[Mutation] Error occurred:', error);
      toast({ description: 'Failed to update preferences', variant: 'destructive' });
    }
  });

  // Reconcile Kudoz wallet mutation - sync locally claimed Kudoz to external API
  const reconcileWallet = useMutation({
    mutationFn: async () => {
      return await apiRequest('POST', '/api/kudoz/reconcile');
    },
    onSuccess: async (data: any) => {
      if (data.success) {
        toast({ 
          description: data.totalAmount > 0 
            ? `Synced ${data.totalAmount} Kudoz to your wallet` 
            : 'Wallet is already synced'
        });
        await queryClient.invalidateQueries({ queryKey: ['/api/kudoz/external-balance'] });
        await queryClient.invalidateQueries({ queryKey: ['/api/kudoz/pending'] });
        await queryClient.invalidateQueries({ queryKey: ['/api/kudoz/transactions'] });
      } else {
        toast({ description: data.error || 'Failed to sync wallet', variant: 'destructive' });
      }
    },
    onError: (error: any) => {
      console.error('[Reconcile] Error:', error);
      toast({ description: 'Failed to sync wallet', variant: 'destructive' });
    }
  });

  // Initialize currentAvatar from user data
  useEffect(() => {
    if (authUser?.avatar) {
      setCurrentAvatar(authUser.avatar);
    }
  }, [authUser?.avatar]);

  // Avatar upload handler
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a JPEG, PNG, GIF, or WebP image.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please select an image under 5MB.',
        variant: 'destructive',
      });
      return;
    }

    setAvatarUploading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const token = localStorage.getItem('auth_token');
      const eatzyToken = localStorage.getItem('eatzyUserToken');
      const response = await fetch('/api/user/avatar', {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(eatzyToken ? { 'x-eatzy-token': eatzyToken } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload avatar');
      }

      const data = await response.json();
      
      if (data.success && data.avatarUrl) {
        setCurrentAvatar(data.avatarUrl);
        // Refresh both the profile context and the global auth user so the
        // avatar updates everywhere (header, nav drawer, etc.)
        await Promise.all([refreshProfile(), refreshUser()]);
        toast({
          title: 'Avatar updated',
          description: 'Your profile picture has been updated.',
        });
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Failed to upload avatar',
        variant: 'destructive',
      });
    } finally {
      setAvatarUploading(false);
      // Reset input so the same file can be selected again
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  // Get most recent order pending review from real order history
  const pendingReviewOrder = orderHistory?.find(order => order.reviewStatus === 'not_reviewed');
  
  // Convert real order to PostOrderReviewPrompt format
  const postOrderData = pendingReviewOrder ? {
    id: pendingReviewOrder.id,
    restaurantId: pendingReviewOrder.restaurantId,
    eatzyClientId: pendingReviewOrder.eatzyClientId,
    restaurantName: pendingReviewOrder.restaurantName,
    type: pendingReviewOrder.type || 'delivery' as const,
    status: 'delivered' as const,
    items: pendingReviewOrder.fullItems || (pendingReviewOrder.items || []).map((item: string | { name?: string }, i: number) => ({
      itemId: `item_${pendingReviewOrder.id}_${i}`,
      itemName: typeof item === 'string' ? item : (item?.name || 'Item'),
      quantity: 1,
      price: pendingReviewOrder.total / (pendingReviewOrder.items?.length || 1)
    })),
    subtotal: pendingReviewOrder.total * 0.85,
    tax: pendingReviewOrder.total * 0.08,
    tip: pendingReviewOrder.total * 0.07,
    total: pendingReviewOrder.total,
    deliveryAddress: pendingReviewOrder.restaurantLocation || '',
    estimatedDeliveryTime: pendingReviewOrder.date?.toISOString() || new Date().toISOString(),
    actualDeliveryTime: pendingReviewOrder.date?.toISOString() || new Date().toISOString(),
    createdAt: pendingReviewOrder.date?.toISOString() || new Date().toISOString()
  } : null;

  // Build user object for TopHeader
  const headerUser = authUser ? {
    id: String(authUser.id),
    name: user.name,
    email: authUser.email,
    avatar: authUser.avatar,
    kudozBalance: user.kudoz,
  } : undefined;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* App Header - Same as Main page */}
      <TopHeader
        user={headerUser}
        unreadAlertCount={(userProfile as any)?.unreadAlertCount || 0}
        unreadMessageCount={unreadMessageData?.count || 0}
        onNotificationsClick={() => {
          const settingsSection = document.getElementById('settings-section');
          if (settingsSection) {
            settingsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        onMessagesClick={() => {
          // Navigate to comments/messages section
          const settingsSection = document.getElementById('settings-section');
          if (settingsSection) {
            settingsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        onProfileClick={() => {
          // Already on profile, scroll to top
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* Scrollable Content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-20">
        <div className="lg:grid lg:grid-cols-[380px_1fr] lg:items-start lg:min-h-full">
        {/* LEFT COLUMN — Profile identity, stats, quick reorder */}
        <div className="lg:border-r lg:border-border lg:sticky lg:top-0 lg:overflow-y-auto lg:max-h-[calc(100vh-4rem)]">
        {/* Hero Section - Creator Identity */}
        <div className="p-4 space-y-4">
          <div className="text-center space-y-4">
            {/* Avatar with Upload */}
            <div className="relative inline-block">
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
                data-testid="input-avatar-file"
              />
              <Avatar className="w-24 h-24 mx-auto">
                <AuthenticatedAvatarImage src={resolveEatzyMediaUrl(currentAvatar || userProfile?.profilePicture || user.avatar)} />
                <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                  {getAvatarInitials(user.firstName, user.lastName, user.name)}
                </AvatarFallback>
              </Avatar>
              {avatarUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-full">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              )}
              <Button
                size="icon"
                variant="secondary"
                className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarUploading}
                data-testid="button-change-avatar"
              >
                <Camera className="w-4 h-4" />
              </Button>
            </div>

            {/* Name & Trust Badge */}
            <div>
              <h2 className="text-2xl font-bold" data-testid="text-user-name">{user.displayName}</h2>
              <p className="text-sm text-muted-foreground">{user.bio}</p>
              <div className="flex items-center justify-center gap-2 mt-3">
                <TrustBadge
                  trustScore={(user as any).trustScore ?? 75}
                  trustTier={(user as any).trustTier || getTrustTier((user as any).trustScore ?? 75)}
                  size="lg"
                  showScore={true}
                  showLabel={true}
                  data-testid="badge-trust"
                />
                <TrustInfoModal />
                <Separator orientation="vertical" className="h-4 mx-1" />
                <Flame className={cn("w-4 h-4", user.streak > 0 ? "text-orange-500" : "text-muted-foreground")} />
                <span className="text-sm font-medium">
                  {user.streak > 0 ? `${user.streak} day streak` : 'No active streak'}
                </span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="text-center" data-testid="stat-reviews">
                <div className="font-bold text-2xl">{user.stats.reviews}</div>
                <div className="text-xs text-muted-foreground">Reviews</div>
              </div>
              <div 
                className="text-center cursor-pointer hover-elevate rounded-lg p-2 -m-2" 
                data-testid="stat-followers"
                onClick={() => {
                  setFollowersModalTab("followers");
                  setShowFollowersModal(true);
                }}
              >
                <div className="font-bold text-2xl">{user.stats.followers}</div>
                <div className="text-xs text-muted-foreground">Followers</div>
              </div>
              <div 
                className="text-center cursor-pointer hover-elevate rounded-lg p-2 -m-2" 
                data-testid="stat-following"
                onClick={() => {
                  setFollowersModalTab("following");
                  setShowFollowersModal(true);
                }}
              >
                <div className="font-bold text-2xl">{user.stats.following}</div>
                <div className="text-xs text-muted-foreground">Following</div>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Unified Kudoz Section */}
        <KudozSection />

        <Separator />

        {/* Your Usuals — top restaurants by order frequency */}
        {(() => {
          type UsualEntry = {
            restaurantId: string;
            restaurantName: string;
            restaurantLogo?: string;
            eatzyClientId: string;
            count: number;
            lastOrder: (typeof orderHistory)[0];
          };
          const usuals = Object.values(
            orderHistory
              .filter(o => o.isCompleted && o.eatzyClientId)
              .reduce((acc, o) => {
                if (!acc[o.restaurantId]) {
                  acc[o.restaurantId] = {
                    restaurantId: o.restaurantId,
                    restaurantName: o.restaurantName,
                    restaurantLogo: o.restaurantLogo,
                    eatzyClientId: o.eatzyClientId || '',
                    count: 0,
                    lastOrder: o,
                  };
                }
                acc[o.restaurantId].count++;
                return acc;
              }, {} as Record<string, UsualEntry>)
          ).sort((a, b) => b.count - a.count).slice(0, 6);

          if (usuals.length === 0) return null;

          return (
            <>
              <div className="p-4 pb-2 space-y-3" data-testid="section-your-usuals">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-lg">Your Usuals</h3>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {usuals.map((usual) => (
                    <Card
                      key={usual.restaurantId}
                      className="flex-shrink-0 w-[150px] hover-elevate active-elevate-2 cursor-pointer"
                      onClick={() => {
                        setReorderModalData({
                          orderId: String(usual.lastOrder.id),
                          restaurantId: usual.restaurantId,
                          eatzyClientId: usual.eatzyClientId,
                          serviceType: usual.lastOrder.type || 'takeout',
                          deliveryLat: usual.lastOrder.deliveryLat,
                          deliveryLng: usual.lastOrder.deliveryLng,
                          deliveryAddressId: usual.lastOrder.deliveryAddressId,
                        });
                      }}
                      data-testid={`usual-${usual.restaurantId}`}
                    >
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          {usual.restaurantLogo ? (
                            <Avatar className="w-7 h-7 flex-shrink-0">
                              <AvatarImage src={usual.restaurantLogo} alt={usual.restaurantName} />
                              <AvatarFallback className="text-xs">{usual.restaurantName.charAt(0)}</AvatarFallback>
                            </Avatar>
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Store className="w-3.5 h-3.5 text-primary" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-xs truncate leading-tight">{usual.restaurantName}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-xs text-muted-foreground">{usual.count}× ordered</span>
                          <Badge variant="secondary" className="text-xs px-1.5">
                            Order
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Separator />
            </>
          );
        })()}

        {/* Settings Section */}
        <div id="settings-section" className="p-4 space-y-3 pb-8">
          <div className="flex items-center gap-2 mb-2">
            <SettingsIcon className="w-5 h-5" />
            <h3 className="font-semibold text-lg">Settings</h3>
          </div>

          <Card className="hover-elevate active-elevate-2 cursor-pointer" onClick={() => navigate('/profile/settings/personal')} data-testid="card-settings-personal">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">Personal Information</span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card className="hover-elevate active-elevate-2 cursor-pointer" onClick={() => navigate('/faq')} data-testid="card-faq">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">Help &amp; FAQ</span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>

          {/* Food Preferences Card */}
          <Card data-testid="card-food-preferences">
            <CardContent className="p-4">
              <div 
                className="flex items-center justify-between cursor-pointer hover-elevate active-elevate-2"
                onClick={() => setShowPreferences(!showPreferences)}
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">Food Preferences</span>
                </div>
                <ChevronRight className={cn("w-5 h-5 text-muted-foreground transition-transform", {
                  "rotate-90": showPreferences
                })} />
              </div>

              {showPreferences && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  {/* Cuisine Preferences */}
                  <div>
                    <h4 className="font-semibold text-sm mb-3">Cuisine Preferences</h4>
                    <div className="space-y-2">
                      {CUISINE_TAGS.map((cuisine) => {
                        const current = preferences?.cuisinePreferences?.[cuisine];
                        return (
                          <div key={cuisine} className="flex items-center justify-between gap-2">
                            <span className="text-sm">{cuisine}</span>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant={current === 'love' ? 'default' : 'outline'}
                                className={cn("h-8 px-2 text-xs", {
                                  "bg-green-600 hover:bg-green-700 text-white": current === 'love'
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updated = { ...(preferences?.cuisinePreferences || {}), [cuisine]: 'love' as const };
                                  updatePreferences.mutate({ cuisinePreferences: updated });
                                }}
                                disabled={updatePreferences.isPending}
                                data-testid={`button-cuisine-love-${cuisine}`}
                              >
                                <Heart className="w-3 h-3 mr-1" />
                                Love
                              </Button>
                              <Button
                                size="sm"
                                variant={current === 'ok' ? 'default' : 'outline'}
                                className="h-8 px-2 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updated = { ...(preferences?.cuisinePreferences || {}), [cuisine]: 'ok' as const };
                                  updatePreferences.mutate({ cuisinePreferences: updated });
                                }}
                                disabled={updatePreferences.isPending}
                                data-testid={`button-cuisine-ok-${cuisine}`}
                              >
                                OK
                              </Button>
                              <Button
                                size="sm"
                                variant={current === 'avoid' ? 'destructive' : 'outline'}
                                className="h-8 px-2 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updated = { ...(preferences?.cuisinePreferences || {}), [cuisine]: 'avoid' as const };
                                  updatePreferences.mutate({ cuisinePreferences: updated });
                                }}
                                disabled={updatePreferences.isPending}
                                data-testid={`button-cuisine-avoid-${cuisine}`}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Avoid
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Dietary Restrictions */}
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold text-sm mb-3">Dietary Restrictions</h4>
                    <div className="flex flex-wrap gap-2">
                      {DIETARY_RESTRICTIONS.map((restriction) => {
                        const isActive = preferences?.dietaryRestrictions?.includes(restriction);
                        return (
                          <Badge
                            key={restriction}
                            variant={isActive ? 'default' : 'outline'}
                            className={cn("cursor-pointer hover-elevate active-elevate-2", {
                              "bg-kudoz text-white": isActive
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              const current = preferences?.dietaryRestrictions || [];
                              const updated = isActive
                                ? current.filter((r: string) => r !== restriction)
                                : [...current, restriction];
                              updatePreferences.mutate({ dietaryRestrictions: updated });
                            }}
                            data-testid={`badge-dietary-${restriction}`}
                          >
                            {restriction}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notifications & Privacy Settings Card */}
          <Card data-testid="card-notifications-privacy">
            <CardContent className="p-4">
              <div 
                className="flex items-center justify-between cursor-pointer hover-elevate active-elevate-2"
                onClick={() => setShowNotificationsPrivacy(!showNotificationsPrivacy)}
              >
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">Notifications & Privacy</span>
                </div>
                <ChevronRight className={cn("w-5 h-5 text-muted-foreground transition-transform", {
                  "rotate-90": showNotificationsPrivacy
                })} />
              </div>

              {showNotificationsPrivacy && (
                <div className="mt-4 pt-4 border-t space-y-6">
                  {isLoadingNotificationPrefs ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      {/* Pause All Notifications */}
                      <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-destructive">Pause All Notifications</div>
                            <div className="text-xs text-muted-foreground">
                              Temporarily stop all notifications. You can still see them in the app.
                            </div>
                          </div>
                          <Switch
                            checked={notificationPrefs.pauseAll}
                            onCheckedChange={(checked) => updateNotificationPref('pauseAll', checked)}
                            data-testid="switch-pause-all"
                          />
                        </div>
                      </div>

                      {/* Section 1: Privacy & Location */}
                      <div>
                        <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-primary" />
                          Privacy & Location
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Location-Based Review Prompts</div>
                              <div className="text-xs text-muted-foreground">
                                Get prompted to leave reviews when you're at a restaurant
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.locationSharing}
                              onCheckedChange={(checked) => updateNotificationPref('locationSharing', checked)}
                              data-testid="switch-location-sharing"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Profile Visibility</div>
                              <div className="text-xs text-muted-foreground">
                                Who can see your reviews and activity
                              </div>
                            </div>
                            <select
                              className={cn(
                                "text-sm bg-background border rounded-md px-2 py-1",
                                notificationPrefs.pauseAll && "opacity-50 cursor-not-allowed"
                              )}
                              value={notificationPrefs.profileVisibility}
                              onChange={(e) => updateNotificationPref('profileVisibility', e.target.value)}
                              disabled={notificationPrefs.pauseAll}
                              data-testid="select-profile-visibility"
                            >
                              <option value="public">Public</option>
                              <option value="friends">Friends Only</option>
                              <option value="private">Private</option>
                            </select>
                          </div>
                          
                          {notificationPrefs.profileVisibility !== 'public' && (
                            <div className="bg-primary/10 border border-primary/20 rounded-md p-3">
                              <p className="text-sm text-primary font-medium">
                                Share your reviews publicly to earn Kudoz and enjoy the best app experience!
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Section 2: Order Updates */}
                      <div>
                        <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                          <Package className="w-4 h-4 text-primary" />
                          Order Updates
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex-1">
                                <div className="font-medium text-sm">Order Confirmations</div>
                                <div className="text-xs text-muted-foreground">
                                  Receive confirmation when your order is placed
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Badge 
                                variant={notificationPrefs.orderConfirmationsPush ? "default" : "outline"} 
                                className="cursor-pointer"
                                onClick={() => updateNotificationPref('orderConfirmationsPush', !notificationPrefs.orderConfirmationsPush)}
                              >
                                Push {notificationPrefs.orderConfirmationsPush && <Check className="w-3 h-3 ml-1" />}
                              </Badge>
                              <Badge 
                                variant={notificationPrefs.orderConfirmationsEmail ? "default" : "outline"} 
                                className="cursor-pointer"
                                onClick={() => updateNotificationPref('orderConfirmationsEmail', !notificationPrefs.orderConfirmationsEmail)}
                              >
                                Email {notificationPrefs.orderConfirmationsEmail && <Check className="w-3 h-3 ml-1" />}
                              </Badge>
                              <Badge 
                                variant={notificationPrefs.orderConfirmationsSms ? "default" : "outline"} 
                                className="cursor-pointer"
                                onClick={() => updateNotificationPref('orderConfirmationsSms', !notificationPrefs.orderConfirmationsSms)}
                              >
                                SMS {notificationPrefs.orderConfirmationsSms && <Check className="w-3 h-3 ml-1" />}
                              </Badge>
                            </div>
                          </div>

                          <div>
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex-1">
                                <div className="font-medium text-sm">Order Status Updates</div>
                                <div className="text-xs text-muted-foreground">
                                  Track when your order is being prepared, out for delivery, etc.
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Badge 
                                variant={notificationPrefs.orderStatusPush ? "default" : "outline"} 
                                className="cursor-pointer"
                                onClick={() => updateNotificationPref('orderStatusPush', !notificationPrefs.orderStatusPush)}
                              >
                                Push {notificationPrefs.orderStatusPush && <Check className="w-3 h-3 ml-1" />}
                              </Badge>
                              <Badge 
                                variant={notificationPrefs.orderStatusEmail ? "default" : "outline"} 
                                className="cursor-pointer"
                                onClick={() => updateNotificationPref('orderStatusEmail', !notificationPrefs.orderStatusEmail)}
                              >
                                Email {notificationPrefs.orderStatusEmail && <Check className="w-3 h-3 ml-1" />}
                              </Badge>
                              <Badge 
                                variant={notificationPrefs.orderStatusSms ? "default" : "outline"} 
                                className="cursor-pointer"
                                onClick={() => updateNotificationPref('orderStatusSms', !notificationPrefs.orderStatusSms)}
                              >
                                SMS {notificationPrefs.orderStatusSms && <Check className="w-3 h-3 ml-1" />}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 3: Review Reminders */}
                      <div>
                        <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                          <Star className="w-4 h-4 text-primary" />
                          Review Reminders
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Dining Prompts</div>
                              <div className="text-xs text-muted-foreground">
                                Remind me to review at a restaurant (requires location)
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.afterDiningPrompts}
                              onCheckedChange={(checked) => updateNotificationPref('afterDiningPrompts', checked)}
                              data-testid="switch-after-dining"
                              disabled={notificationPrefs.pauseAll || !notificationPrefs.locationSharing}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Post-Order Review Reminders</div>
                              <div className="text-xs text-muted-foreground">
                                Remind me to review orders I've received
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.postOrderReminders}
                              onCheckedChange={(checked) => updateNotificationPref('postOrderReminders', checked)}
                              data-testid="switch-post-order"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 4: Social Activity */}
                      <div>
                        <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                          <UsersRound className="w-4 h-4 text-primary" />
                          Social Activity
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Comments & Replies</div>
                              <div className="text-xs text-muted-foreground">
                                When someone comments on or replies to your reviews
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.commentsReplies}
                              onCheckedChange={(checked) => updateNotificationPref('commentsReplies', checked)}
                              data-testid="switch-comments-replies"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Mentions</div>
                              <div className="text-xs text-muted-foreground">
                                When someone mentions you in a review or comment
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.mentions}
                              onCheckedChange={(checked) => updateNotificationPref('mentions', checked)}
                              data-testid="switch-mentions"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">New Followers</div>
                              <div className="text-xs text-muted-foreground">
                                When someone starts following you
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.newFollowers}
                              onCheckedChange={(checked) => updateNotificationPref('newFollowers', checked)}
                              data-testid="switch-new-followers"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Kudoz Earned</div>
                              <div className="text-xs text-muted-foreground">
                                When you earn Kudoz from reviews, badges, or promotions
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.kudozEarned}
                              onCheckedChange={(checked) => updateNotificationPref('kudozEarned', checked)}
                              data-testid="switch-kudoz-earned"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Section 5: Promotions & Marketing */}
                      <div>
                        <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-primary" />
                          Promotions & Marketing
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">Promotional Offers</div>
                              <div className="text-xs text-muted-foreground">
                                Special deals and discounts from restaurants
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.promotionalOffers}
                              onCheckedChange={(checked) => updateNotificationPref('promotionalOffers', checked)}
                              data-testid="switch-promotional-offers"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">New Restaurant Alerts</div>
                              <div className="text-xs text-muted-foreground">
                                When new restaurants open in your area
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.newRestaurantAlerts}
                              onCheckedChange={(checked) => updateNotificationPref('newRestaurantAlerts', checked)}
                              data-testid="switch-new-restaurants"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>

                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">App Updates & News</div>
                              <div className="text-xs text-muted-foreground">
                                New features and platform announcements
                              </div>
                            </div>
                            <Switch
                              checked={notificationPrefs.appUpdatesNews}
                              onCheckedChange={(checked) => updateNotificationPref('appUpdatesNews', checked)}
                              data-testid="switch-app-updates"
                              disabled={notificationPrefs.pauseAll}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Button 
            variant="outline" 
            className="w-full text-destructive hover:text-destructive justify-start"
            data-testid="button-logout"
            onClick={async () => {
              try {
                logout();
                navigate('/');
              } catch (error) {
                console.error('Logout error:', error);
                toast({ 
                  description: 'Failed to log out. Please try again.', 
                  variant: 'destructive' 
                });
              }
            }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>

        </div>{/* end LEFT COLUMN */}
        {/* RIGHT COLUMN — Activity tabs, Creator Tools */}
        <div>
        {/* Activity & History Section */}
        <Separator className="lg:hidden" />
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-lg">Activity & History</h3>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="tabs-activity">
            <TabsList className="w-full grid grid-cols-4 !h-auto">
              <TabsTrigger value="orders" data-testid="tab-orders" className="flex-col gap-0.5 py-2">
                <Package className="w-4 h-4 shrink-0 text-orange-500" />
                <span className="text-xs">Orders</span>
                {!isLoadingOrders && (
                  <span className="text-[10px] text-muted-foreground leading-none">{orderHistory.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="wallet" data-testid="tab-wallet" className="flex-col gap-0.5 py-2">
                <WalletIcon className="w-4 h-4 shrink-0 text-emerald-500" />
                <span className="text-xs">Wallet</span>
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="flex-col gap-0.5 py-2">
                <Star className="w-4 h-4 shrink-0 text-amber-400" />
                <span className="text-xs">Reviews</span>
                <span className="text-[10px] text-muted-foreground leading-none">{user.stats.reviews}</span>
              </TabsTrigger>
              {(() => {
                const unreadCount = alerts.filter((a: any) => !a.read).length;
                return (
                  <TabsTrigger value="alerts" data-testid="tab-alerts" className="relative flex-col gap-0.5 py-2">
                    <Bell className="w-4 h-4 shrink-0 text-blue-500" />
                    <span className="text-xs">Alerts</span>
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })()}
            </TabsList>

            {/* Order History Tab */}
            <TabsContent value="orders" className="space-y-3 mt-4">
              {/* Summary */}
              <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
                {isLoadingOrders ? (
                  <div className="h-4 w-32 bg-muted/50 rounded animate-pulse"></div>
                ) : isOrdersError ? (
                  <span>--</span>
                ) : (
                  <>
                    <span>{orderHistory.length} total orders</span>
                    <span>{orderHistory.filter(o => o.reviewStatus === 'not_reviewed').length} pending reviews</span>
                  </>
                )}
              </div>

              {/* Scrollable Content */}
              <ScrollArea className="h-[60svh] md:h-[70svh] pr-4">
                {isLoadingOrders ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-24 bg-muted/50 rounded animate-pulse"></div>
                    ))}
                  </div>
                ) : isEatzyTokenExpired ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <RefreshCw className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Your session needs refreshing</p>
                    <p className="text-sm mt-1 mb-4">Sign in again to load your order history</p>
                    <Button
                      variant="outline"
                      size="default"
                      onClick={() => navigate('/login?returnUrl=/profile')}
                    >
                      Refresh session
                    </Button>
                  </div>
                ) : isOrdersError ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Unable to load orders</p>
                    <p className="text-sm mt-1">Please try again later</p>
                  </div>
                ) : orderHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No orders yet</p>
                    <p className="text-sm mt-1">Your order history will appear here</p>
                  </div>
                ) : (
                <div className="space-y-3">
                  {orderHistory.slice((ordersPage - 1) * ITEMS_PER_PAGE, ordersPage * ITEMS_PER_PAGE).map((order) => (
                    <Card key={order.id} className="hover-elevate" data-testid={`order-${order.id}`}>
                      <CardContent className="p-4">
                        {/* Header: Restaurant name and Order Status */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold">{order.restaurantName}</h4>
                            <p className="text-sm text-muted-foreground">{order.restaurantLocation}</p>
                          </div>
                          <Badge 
                            variant={order.isCompleted ? 'default' : order.orderStatus === 'open' ? 'secondary' : 'outline'}
                          >
                            {order.orderStatusLabel}
                          </Badge>
                        </div>
                        
                        {/* Order ID, Service Type and Date/Time */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono">Order #{String(order.id).slice(-8).toUpperCase()}</span>
                            {order.type === 'delivery' && (
                              <span className="flex items-center gap-0.5">
                                <Truck className="w-3 h-3" />
                                Delivery
                              </span>
                            )}
                            {order.type === 'takeout' && (
                              <span className="flex items-center gap-0.5">
                                <ShoppingBag className="w-3 h-3" />
                                Takeout
                              </span>
                            )}
                          </div>
                          <span>{order.date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}</span>
                        </div>
                        
                        {/* Order items */}
                        <div className="text-sm text-muted-foreground mb-2">
                          {order.items.join(', ')}
                        </div>
                        
                        {/* Footer: Total and Actions/Status */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">${order.total.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Order Again button for completed orders with ordering enabled */}
                            {order.isCompleted && order.eatzyClientId && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setReorderModalData({
                                    orderId: String(order.id),
                                    restaurantId: order.restaurantId,
                                    eatzyClientId: order.eatzyClientId || '',
                                    serviceType: order.type || 'takeout',
                                    deliveryLat: order.deliveryLat,
                                    deliveryLng: order.deliveryLng,
                                    deliveryAddressId: order.deliveryAddressId,
                                  });
                                }}
                                data-testid={`button-reorder-${order.id}`}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Order Again
                              </Button>
                            )}
                            {/* Show Draft Saved indicator or Write Review button for unreviewed orders */}
                            {order.reviewStatus === 'not_reviewed' && draftOrderId === order.id && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const orderData = {
                                    id: order.id,
                                    restaurantId: order.restaurantId,
                                    eatzyClientId: order.eatzyClientId,
                                    restaurantName: order.restaurantName,
                                    type: order.type,
                                    orderDate: order.date.toISOString(),
                                    items: order.fullItems || order.items.map((itemName, idx) => ({
                                      itemId: `item_${order.id}_${idx}`,
                                      itemName: itemName,
                                      quantity: 1,
                                      price: order.total / order.items.length
                                    }))
                                  };
                                  setSelectedOrderForReview(orderData);
                                  setShowCreateModal(true);
                                }}
                                data-testid={`button-draft-${order.id}`}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Draft Saved
                              </Button>
                            )}
                            {order.reviewStatus === 'not_reviewed' && draftOrderId !== order.id && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const orderData = {
                                    id: order.id,
                                    restaurantId: order.restaurantId,
                                    eatzyClientId: order.eatzyClientId,
                                    restaurantName: order.restaurantName,
                                    type: order.type,
                                    orderDate: order.date.toISOString(),
                                    items: order.fullItems || order.items.map((itemName, idx) => ({
                                      itemId: `item_${order.id}_${idx}`,
                                      itemName: itemName,
                                      quantity: 1,
                                      price: order.total / order.items.length
                                    }))
                                  };
                                  console.log('[Profile] Passing orderData to CreateReviewModal:', orderData);
                                  setSelectedOrderForReview(orderData);
                                  setShowCreateModal(true);
                                }}
                                data-testid={`button-review-${order.id}`}
                              >
                                Write Review
                              </Button>
                            )}
                            {/* Show star rating for reviewed orders */}
                            {order.reviewStatus === 'reviewed' && order.reviewScore && (
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                                <span className="text-sm font-semibold">{order.reviewScore.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                )}
              </ScrollArea>

              {/* Pagination Controls */}
              {!isLoadingOrders && orderHistory.length > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between px-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOrdersPage(prev => Math.max(1, prev - 1))}
                    disabled={ordersPage === 1}
                    data-testid="button-prev-orders"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {ordersPage} of {Math.ceil(orderHistory.length / ITEMS_PER_PAGE)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOrdersPage(prev => Math.min(Math.ceil(orderHistory.length / ITEMS_PER_PAGE), prev + 1))}
                    disabled={ordersPage >= Math.ceil(orderHistory.length / ITEMS_PER_PAGE)}
                    data-testid="button-next-orders"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Wallet Tab */}
            <TabsContent value="wallet" className="space-y-3 mt-4">
              <Card className="bg-gradient-to-br from-kudoz/10 to-kudoz/5">
                <CardContent className="p-6">
                  {pendingKudozQuery.isLoading ? (
                    <div className="space-y-3">
                      <div className="text-sm text-muted-foreground mb-1">Loading balance...</div>
                      <div className="h-12 bg-muted/50 rounded animate-pulse"></div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-kudoz/20">
                        <div className="h-16 bg-muted/50 rounded animate-pulse"></div>
                        <div className="h-16 bg-muted/50 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex-1">
                          <div className="text-sm text-muted-foreground mb-1">Total Balance</div>
                          <div className="text-4xl font-bold text-kudoz flex items-center gap-3">
                            <KudozIcon size="xl" className="w-10 h-10" />
                            {user.kudoz + (pendingKudozQuery.data?.reduce((sum, pk) => sum + pk.amount, 0) || 0)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-kudoz/20">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Available Now</div>
                          <div className="text-xl font-semibold text-kudoz" data-testid="text-available-balance">
                            {user.kudoz}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">Can redeem</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Pending</div>
                          <div className="text-xl font-semibold text-warning" data-testid="text-pending-balance">
                            {pendingKudozQuery.data?.reduce((sum, pk) => sum + pk.amount, 0) || 0}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">Not claimed yet</div>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button 
                      className="flex-1" 
                      onClick={() => navigate('/rewards')}
                      data-testid="button-redeem-kudoz"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Redeem Kudoz
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-shrink-0"
                      onClick={() => reconcileWallet.mutate()}
                      disabled={reconcileWallet.isPending}
                      data-testid="button-sync-wallet"
                    >
                      <WalletIcon className="w-4 h-4 mr-2" />
                      {reconcileWallet.isPending ? 'Syncing...' : 'Sync Wallet'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Gift Certificates Card */}
              <Card className="border-orange-200 dark:border-orange-900/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <CreditCard className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium">Gift Certificates</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Redeem gift certificate codes or purchase gift cards for friends and family.
                  </p>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/profile/gift-certificates')}
                  >
                    View Gift Certificates
                  </Button>
                </CardContent>
              </Card>

              {/* Category Summary Header */}
              {!transactionsQuery.isLoading && categorySummaries.length > 0 && (
                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <div className="text-xs text-muted-foreground mb-2 font-medium">Kudoz by Category</div>
                    <div className="flex flex-wrap gap-2">
                      {categorySummaries.map(summary => (
                        <div 
                          key={summary.category}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-background rounded-md border"
                        >
                          <span className="text-sm font-medium">{summary.label}</span>
                          {summary.earned > 0 && (
                            <span className="text-sm font-semibold text-green-600">+{summary.earned}</span>
                          )}
                          {summary.spent > 0 && (
                            <span className="text-sm font-semibold text-red-600">-{summary.spent}</span>
                          )}
                          <span className="text-xs text-muted-foreground">({summary.count})</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary */}
              <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
                {transactionsQuery.isLoading ? (
                  <div className="h-4 w-32 bg-muted/50 rounded animate-pulse"></div>
                ) : (
                  <>
                    <span>{transactionsQuery.data?.total || 0} total transactions</span>
                    <span className="text-xs">Showing {transactionsQuery.data?.transactions?.length || 0}</span>
                  </>
                )}
              </div>

              {/* Grouped Transactions */}
              <div>
                {transactionsQuery.isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-20 bg-muted/50 rounded animate-pulse"></div>
                    ))}
                  </div>
                ) : groupedTransactions.length > 0 ? (
                  <div className="space-y-3">
                    {groupedTransactions.map((group) => {
                      const isExpanded = expandedMonths.has(group.monthKey);
                      return (
                        <div key={group.monthKey} className="border rounded-lg">
                          {/* Month Header - Clickable to expand/collapse */}
                          <Button
                            variant="ghost"
                            onClick={() => toggleMonthExpansion(group.monthKey)}
                            className="w-full flex items-center justify-between p-3 h-auto rounded-b-none bg-muted/50"
                            data-testid={`month-header-${group.monthKey}`}
                          >
                            <div className="flex items-center gap-2">
                              <ChevronRight className={cn(
                                "w-4 h-4 transition-transform",
                                isExpanded && "rotate-90"
                              )} />
                              <span className="font-medium text-sm">{group.monthLabel}</span>
                              <Badge variant="secondary" className="text-xs">
                                {group.transactions.length}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              {group.totalEarned > 0 && (
                                <span className="text-green-600 font-medium">+{group.totalEarned}</span>
                              )}
                              {group.totalSpent > 0 && (
                                <span className="text-red-600 font-medium">-{group.totalSpent}</span>
                              )}
                            </div>
                          </Button>
                          
                          {/* Transactions List - Collapsible */}
                          {isExpanded && (
                            <div className="divide-y">
                              {group.transactions.map((transaction: any) => (
                                <div 
                                  key={transaction.id} 
                                  className="p-3 bg-background"
                                  data-testid={`transaction-${transaction.id}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 flex-1">
                                      <div className={cn(
                                        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                                        transaction.amount > 0 ? 'bg-green-500/10' : 'bg-red-500/10'
                                      )}>
                                        {transaction.amount > 0 ? (
                                          <ArrowDownLeft className="w-4 h-4 text-green-600" />
                                        ) : (
                                          <ArrowUpRight className="w-4 h-4 text-red-600" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">{transaction.description}</div>
                                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                          <div className="text-xs text-muted-foreground">
                                            {format(new Date(transaction.createdAt), 'MMM d, h:mm a')}
                                          </div>
                                          {transaction.category && (
                                            <Badge variant="outline" className="text-xs py-0">
                                              {transaction.category}
                                            </Badge>
                                          )}
                                          {transaction.status === 'pending' && (
                                            <Badge variant="secondary" className="text-xs py-0 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                              Pending
                                            </Badge>
                                          )}
                                          {transaction.status === 'cancelled' && (
                                            <Badge variant="secondary" className="text-xs py-0 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                              Cancelled
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className={cn(
                                      "font-semibold flex-shrink-0",
                                      transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                                    )}>
                                      {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No transactions yet
                  </div>
                )}
              </div>

              {/* Load More Button */}
              {!transactionsQuery.isLoading && transactionsQuery.data && 
               transactionsQuery.data.transactions?.length < transactionsQuery.data.total && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadMoreTransactions}
                    disabled={transactionsQuery.isFetching}
                    data-testid="button-load-more-transactions"
                  >
                    {transactionsQuery.isFetching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        Load More
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Review History Tab */}
            <TabsContent value="reviews" className="space-y-3 mt-4">
              {/* Summary */}
              <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
                <span>{userReviews.length} total reviews</span>
                <span>{userReviews.reduce((sum, r) => sum + (r.likes || 0), 0)} total likes</span>
              </div>

              {/* Scrollable Content */}
              <ScrollArea className="h-[60svh] md:h-[70svh] pr-4">
                <div className="space-y-3">
                  {isLoadingReviews ? (
                    <div className="text-center py-8 text-muted-foreground">Loading reviews...</div>
                  ) : userReviews.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No reviews yet. Share your dining experiences!
                    </div>
                  ) : (
                    userReviews.slice((reviewsPage - 1) * ITEMS_PER_PAGE, reviewsPage * ITEMS_PER_PAGE).map((review) => (
                      <Card key={review.id} className="hover-elevate" data-testid={`review-${review.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold">{review.restaurantName || (typeof review.restaurant === 'object' ? (review.restaurant as any)?.name : review.restaurant) || 'Restaurant'}</h3>
                              <div className="flex items-center gap-1 mt-1">
                                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                <span className="font-semibold">{(review.rating || 0).toFixed(1)}</span>
                                {review.isEdited && <span className="text-xs text-muted-foreground">(edited)</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="text-xs text-muted-foreground">
                                {review.createdAt ? formatDistanceToNow(new Date(review.createdAt), { addSuffix: true }) : ''}
                              </span>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground"
                                    aria-label="Review options"
                                    data-testid={`button-review-options-${review.id}`}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => setEditingReview({ id: review.id, rating: review.rating || 5, text: review.text || '' })}
                                    data-testid={`menu-item-edit-${review.id}`}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit Review
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => setDeletingReviewId(review.id)}
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`menu-item-delete-${review.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Review
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{review.text}</p>
                          {(() => {
                            const mediaUrl = review.images?.[0] || review.mediaUrl;
                            if (!mediaUrl) return null;
                            const isVideo = review.mediaType === 'video' || /\.(mp4|mov|webm)(\?|$)/i.test(mediaUrl);
                            return isVideo ? (
                              <AuthenticatedVideo
                                src={mediaUrl}
                                className="w-full h-40 object-cover rounded-md mb-3"
                                controls
                                playsInline
                                preload="metadata"
                              />
                            ) : (
                              <img
                                src={proxyIfCdn(mediaUrl) ?? mediaUrl}
                                alt="Review"
                                className="w-full h-40 object-cover rounded-md mb-3"
                              />
                            );
                          })()}
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Heart className="w-4 h-4" />
                            <span>{review.likes || 0} likes</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>

              {/* Pagination Controls */}
              {userReviews.length > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between px-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReviewsPage(prev => Math.max(1, prev - 1))}
                    disabled={reviewsPage === 1}
                    data-testid="button-prev-reviews"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {reviewsPage} of {Math.ceil(userReviews.length / ITEMS_PER_PAGE)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReviewsPage(prev => Math.min(Math.ceil(userReviews.length / ITEMS_PER_PAGE), prev + 1))}
                    disabled={reviewsPage >= Math.ceil(userReviews.length / ITEMS_PER_PAGE)}
                    data-testid="button-next-reviews"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts" className="space-y-2 mt-4">
              <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
                {isLoadingAlerts ? (
                  <div className="h-4 w-32 bg-muted/50 rounded animate-pulse"></div>
                ) : (
                  <>
                    <span>{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</span>
                    {alerts.filter((a: any) => !a.read).length > 0 && (
                      <span className="text-destructive font-medium">{alerts.filter((a: any) => !a.read).length} unread</span>
                    )}
                  </>
                )}
              </div>
              {isLoadingAlerts ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-16 bg-muted/50 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No alerts yet</p>
                  <p className="text-sm mt-1">Kudoz rewards and activity notifications appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {alerts.map((alert: any) => (
                    <div
                      key={alert.id}
                      className={cn(
                        "flex items-start justify-between gap-2 p-3 rounded-lg border",
                        !alert.read && "bg-primary/5 border-primary/20"
                      )}
                      data-testid={`alert-tab-${alert.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs capitalize">{alert.category}</Badge>
                          {!alert.read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                          <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}</span>
                        </div>
                        <p className="text-sm">{alert.message}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!alert.read && (
                          <Button size="icon" variant="ghost" onClick={() => markAlertAsRead.mutate(alert.id)} disabled={markAlertAsRead.isPending} data-testid={`button-mark-read-tab-${alert.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => deleteAlert.mutate(alert.id)} disabled={deleteAlert.isPending} data-testid={`button-dismiss-tab-${alert.id}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <Separator />

        {/* Creator Toolbox Section */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Edit3 className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-lg">Creator Tools</h3>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(true)} data-testid="card-create-review">
              <Plus className="w-4 h-4 mr-2" />
              Create Review
            </Button>
            <Button variant="outline" className="flex-1 relative" onClick={() => setShowDraftManager(true)} data-testid="card-drafts">
              <Edit3 className="w-4 h-4 mr-2" />
              Review Center
              {reviewCenterCount > 0 && (
                <Badge variant="secondary" className="ml-1">{reviewCenterCount}</Badge>
              )}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setShowCommentsManager(true)} data-testid="card-comments">
              <Bell className="w-4 h-4 mr-2" />
              Messages
            </Button>
          </div>

          <button
            className="flex items-center justify-between w-full cursor-pointer pt-1"
            onClick={() => setShowCreatorInsights(v => !v)}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-green-600" />
              <span className="font-medium text-sm">Creator Insights</span>
            </div>
            {showCreatorInsights ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {showCreatorInsights && (
            <div className="space-y-3 mt-1">
              {/* Engagement Stats */}
              <div className="grid grid-cols-2 gap-3">
                <Card data-testid="card-total-views">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold">{user.stats.totalViews.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Total Views</div>
                  </CardContent>
                </Card>
                <Card data-testid="card-total-likes">
                  <CardContent className="p-4 text-center">
                    <Heart className="w-6 h-6 text-red-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold">{user.stats.totalLikes.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Total Likes</div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Performing Posts */}
              <Card data-testid="card-top-posts">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-yellow-600" />
                    <h4 className="font-medium">Top Performing Reviews</h4>
                  </div>
                  {isLoadingReviews ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-muted animate-pulse" />
                            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                          </div>
                          <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                        </div>
                      ))}
                    </div>
                  ) : topReviews.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">Post your first review to see your top performing content.</p>
                  ) : (
                    topReviews.map((review, index) => (
                      <div key={review.id} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="w-6 h-6 rounded-full p-0 flex items-center justify-center">
                            {index + 1}
                          </Badge>
                          <span className="text-sm font-medium">{review.restaurant}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {review.rating}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {review.likes}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Brand Partnerships */}
              <Card className="bg-gradient-to-br from-blue-500/10 to-purple-500/10" data-testid="card-collaborations">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">Brand Partnerships</div>
                      <div className="text-sm text-muted-foreground">Unlock at 500 followers</div>
                    </div>
                    <Target className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <Separator />

        {/* My Lists Section */}
        <div className="p-4 space-y-3">
          <button
            className="flex items-center justify-between w-full mb-2 cursor-pointer"
            onClick={() => setShowLists(v => !v)}
          >
            <div className="flex items-center gap-2">
              <List className="w-5 h-5 text-kudoz" />
              <h3 className="font-semibold text-lg">My Lists</h3>
            </div>
            {showLists ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {/* List Categories Grid */}
          {showLists && <div className="grid grid-cols-2 gap-3">
            {/* The Best Restaurants */}
            <Card 
              className="hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => setSelectedList('best-restaurants')}
              data-testid="list-best-restaurants"
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-yellow-500/10 rounded-full p-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">The Best</h4>
                  <p className="text-xs text-muted-foreground">Restaurants</p>
                </div>
                <div className="text-2xl font-bold text-kudoz">
                  {userReviews.filter((r: any) => r.tags?.includes('The Best')).length}
                </div>
              </CardContent>
            </Card>

            {/* The Best Items */}
            <Card 
              className="hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => setSelectedList('best-items')}
              data-testid="list-best-items"
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-yellow-500/10 rounded-full p-2">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">The Best</h4>
                  <p className="text-xs text-muted-foreground">Items</p>
                </div>
                <div className="text-2xl font-bold text-kudoz">
                  {userReviews.filter((r: any) => r.tags?.includes('The Best') && r.itemName).length}
                </div>
              </CardContent>
            </Card>

            {/* Top-Rated Restaurants */}
            <Card 
              className="hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => setSelectedList('top-rated')}
              data-testid="list-top-rated"
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-yellow-500/10 rounded-full p-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Top-Rated</h4>
                  <p className="text-xs text-muted-foreground">Restaurants</p>
                </div>
                <div className="text-2xl font-bold text-kudoz">
                  {userReviews.filter((r: any) => r.rating >= 8).length}
                </div>
              </CardContent>
            </Card>

            {/* Saved Restaurants */}
            <Card 
              className="hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => setSelectedList('saved')}
              data-testid="list-saved"
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-kudoz/10 rounded-full p-2">
                    <BookmarkIcon className="w-5 h-5 text-kudoz" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Saved</h4>
                  <p className="text-xs text-muted-foreground">Restaurants</p>
                </div>
                <div className="text-2xl font-bold text-kudoz">
                  {bookmarks.length}
                </div>
              </CardContent>
            </Card>

            {/* Most Ordered Restaurants */}
            <Card 
              className="hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => setSelectedList('most-ordered-restaurants')}
              data-testid="list-most-ordered-restaurants"
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500/10 rounded-full p-2">
                    <Package className="w-5 h-5 text-blue-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Most Ordered</h4>
                  <p className="text-xs text-muted-foreground">Restaurants</p>
                </div>
                <div className="text-2xl font-bold text-kudoz">
                  {(() => {
                    const restaurantCounts = orderHistory.reduce((acc: Record<string, number>, order: any) => {
                      const name = order.restaurantName || order.restaurant?.name || 'Unknown';
                      acc[name] = (acc[name] || 0) + 1;
                      return acc;
                    }, {});
                    return Object.keys(restaurantCounts).length;
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Most Ordered Items */}
            <Card 
              className="hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => setSelectedList('most-ordered-items')}
              data-testid="list-most-ordered-items"
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500/10 rounded-full p-2">
                    <ShoppingBag className="w-5 h-5 text-blue-500" />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Most Ordered</h4>
                  <p className="text-xs text-muted-foreground">Items</p>
                </div>
                <div className="text-2xl font-bold text-kudoz">
                  {(() => {
                    const allItems = orderHistory.flatMap((o: any) => o.items?.map((item: any) => item.name) || []);
                    const itemCounts = allItems.reduce((acc: Record<string, number>, item: string) => {
                      acc[item] = (acc[item] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>);
                    return Object.entries(itemCounts).filter(([_, count]) => (count as number) >= 2).length;
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>}
        </div>

        <Separator />

        {/* Badges & Achievements Section */}
        <div className="p-4 space-y-3">
          <button
            className="flex items-center justify-between w-full mb-2 cursor-pointer"
            onClick={() => setShowBadges(v => !v)}
          >
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-kudoz" />
              <h3 className="font-semibold text-lg">Badges & Achievements</h3>
            </div>
            {showBadges ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {/* Badge Categories with Progress */}
          {showBadges && <div className="space-y-3">
            {/* Summary */}
            <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
              <span>
                {userBadges.reduce((acc, ub) => {
                  if (!acc.includes(ub.badge.category)) acc.push(ub.badge.category);
                  return acc;
                }, [] as string[]).length} categories progressing
              </span>
            </div>

            {isLoadingBadges || isLoadingProgress ? (
              <div className="space-y-3">
                <div className="h-32 bg-muted/50 rounded-md animate-pulse"></div>
                <div className="h-32 bg-muted/50 rounded-md animate-pulse"></div>
                <div className="h-32 bg-muted/50 rounded-md animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-3">
                  {/* Group all badges by category and show progress */}
                  {(() => {
                    return getAllBadgeCategories().map((category) => {
                      // Find the highest tier earned in this category
                      const earnedInCategory = userBadges.filter(ub => ub.badge.category === category);
                      const tierOrder = { bronze: 1, silver: 2, gold: 3, platinum: 4 };
                      const currentBadge = earnedInCategory.length > 0 
                        ? earnedInCategory.reduce((highest, current) => 
                            tierOrder[current.badge.tier as keyof typeof tierOrder] > 
                            tierOrder[highest.badge.tier as keyof typeof tierOrder] 
                              ? current : highest
                          )
                        : null;
                      
                      // Find the next badge to earn in this category
                      const nextBadge = badgeProgress.find(
                        p => p.badge.category === category && !p.isUnlocked
                      );

                      const IconComponent = getBadgeCategoryIcon(category);
                      const accentColor = getCategoryAccentColor(category);

                      return (
                        <Card 
                          key={category}
                          className={cn(
                            "border-2",
                            currentBadge && getBadgeTierColor(currentBadge.badge.tier)
                          )}
                          data-testid={`badge-category-card-${category}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div 
                                className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${accentColor}20` }}
                              >
                                <IconComponent className="w-7 h-7" style={{ color: accentColor }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h4 className="font-semibold">{getCategoryName(category)}</h4>
                                  {currentBadge && (
                                    <Badge variant="secondary" className="capitalize flex-shrink-0">
                                      {currentBadge.badge.tier}
                                    </Badge>
                                  )}
                                </div>
                                
                                {currentBadge && (
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {currentBadge.badge.name} - {currentBadge.badge.description}
                                  </p>
                                )}

                                {nextBadge && (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                      <span>Next: {nextBadge.badge.name}</span>
                                      <span className="font-medium">{Math.min(100, Math.round(nextBadge.percentage ?? 0))}%</span>
                                    </div>
                                    <Progress value={Math.min(100, nextBadge.percentage ?? 0)} className="h-2" />
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                      <span>{(nextBadge.currentValue ?? 0).toLocaleString()} / {(nextBadge.thresholdValue ?? 0).toLocaleString()}</span>
                                      <div className="flex items-center gap-1">
                                        <KudozIcon size="xs" />
                                        <span className="font-medium text-kudoz">+{nextBadge.badge.kudozReward}</span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {!nextBadge && currentBadge?.badge.tier === 'platinum' && (
                                  <div className="flex items-center gap-2 text-xs font-medium text-kudoz">
                                    <Trophy className="w-3 h-3" />
                                    <span>Max tier achieved!</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    });
                  })()}

                  {/* Empty state */}
                  {userBadges.length === 0 && (
                    <div className="text-center py-12">
                      <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <h4 className="font-semibold mb-1">Start Earning Badges</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Review restaurants and place orders to unlock badges!
                      </p>
                      <BadgeExplainerModal />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>}
        </div>

        </div>{/* end RIGHT COLUMN */}
        </div>{/* end lg:grid */}
      </div>{/* end scrollRef */}


      {/* Modals */}
      <DraftReviewsManager
        isOpen={showDraftManager}
        onClose={() => setShowDraftManager(false)}
        onReviewSelected={(reviewData) => {
          console.log('Draft review selected for editing:', reviewData);
          setShowCreateModal(true);
        }}
        eatzyOrders={orderHistory.map(order => ({
          id: String(order.id),
          hash: String(order.id),
          restaurantName: order.restaurantName,
          restaurantId: order.restaurantId,
          eatzyClientId: order.eatzyClientId,
          items: order.items,
          fullItems: order.fullItems,
          total: order.total,
          status: order.orderStatus,
          date: order.date,
          type: order.type
        }))}
        reviewedOrderIds={reviewedOrderIds}
      />

      <CommentsManager
        isOpen={showCommentsManager}
        onClose={() => setShowCommentsManager(false)}
      />

      {postOrderData && (
        <PostOrderReviewPrompt
          isOpen={showPostOrderPrompt}
          onClose={() => setShowPostOrderPrompt(false)}
          orderData={postOrderData}
          onReviewSubmitted={() => {
            setShowPostOrderPrompt(false);
            // Invalidate reviewed order IDs to update review status in order list
            queryClient.invalidateQueries({ queryKey: ['/api/user/reviewed-order-ids'] });
            console.log('Post-order review submitted');
          }}
          onSkip={() => setShowPostOrderPrompt(false)}
        />
      )}

      <CreateReviewModal
        isOpen={showCreateModal || !!selectedOrderForReview}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedOrderForReview(null);
        }}
        onReviewSubmitted={() => {
          setShowCreateModal(false);
          setSelectedOrderForReview(null);
          // Invalidate reviewed order IDs to update review status in order list
          queryClient.invalidateQueries({ queryKey: ['/api/user/reviewed-order-ids'] });
          console.log('Review submitted');
        }}
        detectedLocation="Downtown NYC"
        orderData={selectedOrderForReview}
        restaurantId={selectedOrderForReview?.restaurantId}
        restaurantName={selectedOrderForReview?.restaurantName}
      />

      {/* Reorder Modal */}
      {reorderModalData && (
        <ReorderValidationModal
          isOpen={!!reorderModalData}
          onClose={() => setReorderModalData(null)}
          orderId={reorderModalData.orderId}
          restaurantId={reorderModalData.restaurantId}
          eatzyClientId={reorderModalData.eatzyClientId}
          serviceType={reorderModalData.serviceType}
          deliveryLat={reorderModalData.deliveryLat}
          deliveryLng={reorderModalData.deliveryLng}
          deliveryAddressId={reorderModalData.deliveryAddressId}
          onSuccess={(info) => {
            setReorderModalData(null);
            if (info.action === 'modify' && info.restaurantId) {
              const isUuid = info.restaurantId.includes('-');
              navigate(isUuid ? `/restaurant/${info.restaurantId}` : `/r/${info.restaurantId}`);
            } else {
              navigate('/checkout');
            }
          }}
        />
      )}

      {/* My Lists Detail Sheet */}
      <Sheet open={!!selectedList} onOpenChange={(open) => !open && setSelectedList(null)}>
        <SheetContent side="bottom" className="h-[80svh]" data-testid="sheet-list-details">
          <SheetHeader>
            <SheetTitle>
              {selectedList === 'best-restaurants' && 'The Best Restaurants'}
              {selectedList === 'best-items' && 'The Best Items'}
              {selectedList === 'top-rated' && 'Top-Rated Restaurants'}
              {selectedList === 'saved' && 'Saved Restaurants'}
              {selectedList === 'most-ordered-restaurants' && 'Most Ordered Restaurants'}
              {selectedList === 'most-ordered-items' && 'Most Ordered Items'}
            </SheetTitle>
          </SheetHeader>
          
          <ScrollArea className="h-full mt-4 pr-4">
            <div className="space-y-3 pb-20">
              {/* The Best Restaurants - tagged with "The Best" */}
              {selectedList === 'best-restaurants' && (
                <>
                  {userReviews.filter((r: any) => r.tags?.includes('The Best')).length === 0 ? (
                    <div className="text-center py-12">
                      <Trophy className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No "The Best" Tagged Yet</h3>
                      <p className="text-muted-foreground">Tag a review with "The Best" to add it here!</p>
                    </div>
                  ) : (
                    userReviews.filter((r: any) => r.tags?.includes('The Best')).map((review: any) => (
                      <Card key={review.id} className="hover-elevate cursor-pointer" onClick={() => navigate(`/restaurant/${review.restaurantId}`)}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold">{review.restaurant?.name || 'Restaurant'}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{review.content}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {review.createdAt && formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Trophy className="w-4 h-4 text-yellow-500" />
                              <span className="font-semibold">{review.rating?.toFixed(1)}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </>
              )}

              {/* Top-Rated Restaurants */}
              {selectedList === 'top-rated' && (
                <>
                  {userReviews.filter((r: any) => r.rating >= 8).length === 0 ? (
                    <div className="text-center py-12">
                      <Star className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Top-Rated Reviews Yet</h3>
                      <p className="text-muted-foreground">Rate restaurants 8+ to add them to your top-rated list!</p>
                    </div>
                  ) : (
                    userReviews.filter((r: any) => r.rating >= 8).sort((a: any, b: any) => b.rating - a.rating).map((review: any) => (
                      <Card key={review.id} className="hover-elevate cursor-pointer" onClick={() => navigate(`/restaurant/${review.restaurantId}`)}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold">{review.restaurant?.name || 'Restaurant'}</h3>
                              <p className="text-sm text-muted-foreground">{review.restaurant?.cuisine || ''}</p>
                              <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{review.content}</p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                              <span className="font-semibold">{review.rating?.toFixed(1)}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </>
              )}

              {/* Saved Restaurants */}
              {selectedList === 'saved' && (
                <>
                  {bookmarks.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="bg-kudoz/10 rounded-full p-6 mb-4 inline-block">
                        <BookmarkIcon className="w-12 h-12 text-kudoz" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Start Building Your Restaurant List</h3>
                      <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                        Save spots as you browse to create your personal food guide. Earn 10 Kudoz bonus for your first save!
                      </p>
                      <Button onClick={() => {
                        setSelectedList(null);
                        navigate('/?tab=discovery');
                      }}>
                        <Search className="w-4 h-4 mr-2" />
                        Explore Restaurants
                      </Button>
                    </div>
                  ) : (
                    bookmarks.map((bookmark: any) => bookmark.restaurant && (
                      <Card key={bookmark.id} className="hover-elevate cursor-pointer" onClick={() => navigate(`/restaurant/${bookmark.restaurant.id}`)}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold">{bookmark.restaurant.name}</h3>
                              <p className="text-sm text-muted-foreground">{bookmark.restaurant.cuisine}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Saved {formatDistanceToNow(new Date(bookmark.createdAt), { addSuffix: true })}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleBookmark.mutate(bookmark.restaurant.id);
                              }}
                              disabled={toggleBookmark.isPending}
                            >
                              <BookmarkIcon className="w-4 h-4 fill-current text-kudoz" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </>
              )}

              {/* Most Ordered Restaurants */}
              {selectedList === 'most-ordered-restaurants' && (
                <>
                  {(() => {
                    const restaurantData = orderHistory.reduce((acc: Record<string, { count: number, id?: string }>, order: any) => {
                      const name = order.restaurantName || order.restaurant?.name || 'Unknown';
                      const id = order.restaurantId || order.restaurant?.id;
                      if (!acc[name]) {
                        acc[name] = { count: 0, id };
                      }
                      acc[name].count += 1;
                      return acc;
                    }, {});
                    const sortedRestaurants = Object.entries(restaurantData).sort(([,a], [,b]) => b.count - a.count);
                    
                    return sortedRestaurants.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No Orders Yet</h3>
                        <p className="text-muted-foreground">Order from restaurants to build this list!</p>
                      </div>
                    ) : (
                      sortedRestaurants.map(([name, data]) => (
                        <Card key={name} className="hover-elevate cursor-pointer" onClick={() => data.id && navigate(`/restaurant/${data.id}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold">{name}</h3>
                                <p className="text-sm text-muted-foreground">{data.count} order{data.count !== 1 ? 's' : ''}</p>
                              </div>
                              <Badge variant="secondary">{data.count}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    );
                  })()}
                </>
              )}

              {/* Most Ordered Items */}
              {selectedList === 'most-ordered-items' && (
                <>
                  {(() => {
                    const allItems = orderHistory.flatMap((o: any) => o.items?.map((item: any) => item.name) || []);
                    const itemCounts = allItems.reduce((acc: Record<string, number>, item: string) => {
                      acc[item] = (acc[item] || 0) + 1;
                      return acc;
                    }, {});
                    const sortedItems = Object.entries(itemCounts).filter(([_, count]) => (count as number) >= 2).sort(([,a], [,b]) => (b as number) - (a as number));
                    
                    return sortedItems.length === 0 ? (
                      <div className="text-center py-12">
                        <ShoppingBag className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No Repeat Orders Yet</h3>
                        <p className="text-muted-foreground">Order the same items multiple times to build this list!</p>
                      </div>
                    ) : (
                      sortedItems.map(([item, count]) => (
                        <Card key={item} className="hover-elevate">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold">{item}</h3>
                                <p className="text-sm text-muted-foreground">Ordered {count as number} times</p>
                              </div>
                              <Badge variant="secondary">{count as number}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    );
                  })()}
                </>
              )}

              {/* The Best Items - items from reviews tagged with "The Best" */}
              {selectedList === 'best-items' && (
                <>
                  {(() => {
                    const bestItemReviews = userReviews.filter((r: any) => r.tags?.includes('The Best') && r.itemName);
                    
                    return bestItemReviews.length === 0 ? (
                      <div className="text-center py-12">
                        <Sparkles className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">No "The Best" Items Yet</h3>
                        <p className="text-sm text-muted-foreground">
                          Tag an item review with "The Best" to add it here!
                        </p>
                      </div>
                    ) : (
                      bestItemReviews.map((review: any) => (
                        <Card key={review.id} className="hover-elevate cursor-pointer" onClick={() => navigate(`/restaurant/${review.restaurantId}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold">{review.itemName}</h3>
                                <p className="text-sm text-muted-foreground">
                                  From {review.restaurant?.name || 'Restaurant'}
                                </p>
                                <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{review.content}</p>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                <Sparkles className="w-4 h-4 text-yellow-500" />
                                <span className="font-semibold">{review.rating?.toFixed(1)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    );
                  })()}
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Followers/Following Modal */}
      <FollowersListModal
        isOpen={showFollowersModal}
        onClose={() => setShowFollowersModal(false)}
        userId={String(user.id)}
        initialTab={followersModalTab}
        followersCount={user.stats.followers}
        followingCount={user.stats.following}
      />

      {/* Edit Review Modal */}
      <Dialog open={!!editingReview} onOpenChange={(open) => { if (!open) setEditingReview(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Review</DialogTitle>
            <DialogDescription>Update your rating or review text.</DialogDescription>
          </DialogHeader>
          {editingReview && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="profile-edit-rating">Rating (1–10)</Label>
                <div className="flex items-center gap-3">
                  <input
                    id="profile-edit-rating"
                    type="range"
                    min={1}
                    max={10}
                    step={0.5}
                    value={editingReview.rating}
                    onChange={(e) => setEditingReview(prev => prev ? { ...prev, rating: parseFloat(e.target.value) } : null)}
                    className="flex-1 accent-kudoz"
                    data-testid="profile-edit-rating-slider"
                  />
                  <span className="text-sm font-bold w-8 text-right">{editingReview.rating.toFixed(1)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-edit-text">Review Text</Label>
                <Textarea
                  id="profile-edit-text"
                  value={editingReview.text}
                  onChange={(e) => setEditingReview(prev => prev ? { ...prev, text: e.target.value } : null)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Share your experience..."
                  className="resize-none"
                  data-testid="profile-edit-review-text"
                />
                <p className="text-xs text-muted-foreground text-right">{editingReview.text.length}/2000</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditingReview(null)}
              disabled={editReviewMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => editingReview && editReviewMutation.mutate(editingReview)}
              disabled={editReviewMutation.isPending || !editingReview}
              data-testid="button-profile-confirm-edit-review"
            >
              {editReviewMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Review Confirm Dialog */}
      <Dialog open={!!deletingReviewId} onOpenChange={(open) => { if (!open) setDeletingReviewId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Review</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this review? Any Kudoz earned from it will be reversed — including Kudoz already credited to your wallet. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeletingReviewId(null)}
              disabled={deleteReviewMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingReviewId && deleteReviewMutation.mutate(deletingReviewId)}
              disabled={deleteReviewMutation.isPending}
              data-testid="button-profile-confirm-delete-review"
            >
              {deleteReviewMutation.isPending ? 'Deleting...' : 'Delete Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
